import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';
import './DriverForm.css';

const DriverForm = ({ driver, cities, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    city: '',
    status: 'active',
    license_number: '',
    vehicle_type: '',
    vehicle_plate: '',
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isEditMode = !!driver;

  // Initialize form data
  useEffect(() => {
    if (driver) {
      setFormData({
        first_name: driver.first_name || '',
        last_name: driver.last_name || '',
        email: driver.email || '',
        password: '', // Never pre-fill password
        phone: driver.phone || '',
        city: driver.city || '',
        status: driver.status || 'active',
        license_number: driver.license_number || '',
        vehicle_type: driver.vehicle_type || '',
        vehicle_plate: driver.vehicle_plate || '',
        notes: driver.notes || ''
      });
    }
  }, [driver]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Ad zorunludur';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Soyad zorunludur';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email zorunludur';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Geçerli bir email adresi giriniz';
    }
    if (!isEditMode && !formData.password.trim()) {
      newErrors.password = 'Şifre zorunludur';
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Şifre en az 6 karakter olmalıdır';
    }

    // Phone validation (optional but if provided must be valid)
    if (formData.phone && !/^[\d\s\+\-\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Geçerli bir telefon numarası giriniz';
    }

    // License number (optional but if provided must be valid)
    if (formData.license_number && formData.license_number.length < 5) {
      newErrors.license_number = 'Ehliyet numarası en az 5 karakter olmalıdır';
    }

    // Vehicle plate (optional but if provided must be valid)
    if (formData.vehicle_plate && !/^[A-Z0-9\s]+$/i.test(formData.vehicle_plate)) {
      newErrors.vehicle_plate = 'Geçerli bir plaka giriniz (örn: 34 ABC 123)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare data - remove empty password for edit mode
      const submitData = { ...formData };
      if (isEditMode && !submitData.password) {
        delete submitData.password;
      }

      await onSubmit(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
      // Handle API errors
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ 
          general: error.response?.data?.message || 'Bir hata oluştu' 
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const vehicleTypes = [
    { value: '', label: 'Araç türü seçiniz' },
    { value: 'sedan', label: 'Sedan' },
    { value: 'suv', label: 'SUV' },
    { value: 'hatchback', label: 'Hatchback' },
    { value: 'wagon', label: 'Station Wagon' },
    { value: 'minivan', label: 'Minivan' },
    { value: 'luxury', label: 'Lüks Araç' },
    { value: 'electric', label: 'Elektrikli Araç' },
    { value: 'hybrid', label: 'Hibrit Araç' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Aktif', color: '#10b981' },
    { value: 'inactive', label: 'İnaktif', color: '#ef4444' },
    { value: 'suspended', label: 'Askıya Alınmış', color: '#f59e0b' }
  ];

  return (
    <div className="driver-form">
      <div className="form-header">
        <h3>
          {isEditMode ? '✏️ Taxifahrer Düzenle' : '➕ Yeni Taxifahrer Ekle'}
        </h3>
        <p className="form-subtitle">
          {isEditMode 
            ? `${driver.first_name} ${driver.last_name} bilgilerini güncelleyin`
            : 'Yeni taxifahrer bilgilerini doldurun'
          }
        </p>
      </div>

      {errors.general && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="driver-form-content">
        {/* Personal Information */}
        <div className="form-section">
          <h4 className="section-title">👤 Kişisel Bilgiler</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="first_name">
                Ad <span className="required">*</span>
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className={errors.first_name ? 'error' : ''}
                placeholder="Taxifahrer adı"
                disabled={isSubmitting}
              />
              {errors.first_name && (
                <span className="error-text">{errors.first_name}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="last_name">
                Soyad <span className="required">*</span>
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className={errors.last_name ? 'error' : ''}
                placeholder="Taxifahrer soyadı"
                disabled={isSubmitting}
              />
              {errors.last_name && (
                <span className="error-text">{errors.last_name}</span>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="form-section">
          <h4 className="section-title">📧 İletişim Bilgileri</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="email">
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? 'error' : ''}
                placeholder="ornek@email.com"
                disabled={isSubmitting}
              />
              {errors.email && (
                <span className="error-text">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="phone">
                Telefon
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={errors.phone ? 'error' : ''}
                placeholder="+90 555 123 45 67"
                disabled={isSubmitting}
              />
              {errors.phone && (
                <span className="error-text">{errors.phone}</span>
              )}
            </div>
          </div>
        </div>

        {/* Login Information */}
        <div className="form-section">
          <h4 className="section-title">🔐 Giriş Bilgileri</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="password">
                {isEditMode ? 'Yeni Şifre (Boş bırakılırsa değişmez)' : 'Şifre'} 
                {!isEditMode && <span className="required">*</span>}
              </label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={errors.password ? 'error' : ''}
                  placeholder={isEditMode ? 'Yeni şifre (opsiyonel)' : 'Güçlü bir şifre'}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isSubmitting}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.password && (
                <span className="error-text">{errors.password}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="status">
                Durum <span className="required">*</span>
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className={errors.status ? 'error' : ''}
                disabled={isSubmitting}
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.status && (
                <span className="error-text">{errors.status}</span>
              )}
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div className="form-section">
          <h4 className="section-title">🌍 Lokasyon Bilgileri</h4>
          <div className="form-group">
            <label htmlFor="city">
              Şehir <span className="required">*</span>
            </label>
            <select
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              className={errors.city ? 'error' : ''}
              disabled={isSubmitting}
            >
              <option value="">Şehir seçiniz</option>
              {cities.map(city => (
                <option key={city.city} value={city.city}>
                  {city.city} ({city.count} taxifahrer)
                </option>
              ))}
            </select>
            {errors.city && (
              <span className="error-text">{errors.city}</span>
            )}
          </div>
        </div>

        {/* Vehicle Information */}
        <div className="form-section">
          <h4 className="section-title">🚗 Araç Bilgileri</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="license_number">
                Ehliyet Numarası
              </label>
              <input
                type="text"
                id="license_number"
                name="license_number"
                value={formData.license_number}
                onChange={handleInputChange}
                className={errors.license_number ? 'error' : ''}
                placeholder="Ehliyet numarası"
                disabled={isSubmitting}
              />
              {errors.license_number && (
                <span className="error-text">{errors.license_number}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="vehicle_type">
                Araç Türü
              </label>
              <select
                id="vehicle_type"
                name="vehicle_type"
                value={formData.vehicle_type}
                onChange={handleInputChange}
                className={errors.vehicle_type ? 'error' : ''}
                disabled={isSubmitting}
              >
                {vehicleTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.vehicle_type && (
                <span className="error-text">{errors.vehicle_type}</span>
              )}
            </div>

            <div className="form-group full-width">
              <label htmlFor="vehicle_plate">
                Araç Plakası
              </label>
              <input
                type="text"
                id="vehicle_plate"
                name="vehicle_plate"
                value={formData.vehicle_plate}
                onChange={handleInputChange}
                className={errors.vehicle_plate ? 'error' : ''}
                placeholder="34 ABC 123"
                disabled={isSubmitting}
                style={{ textTransform: 'uppercase' }}
              />
              {errors.vehicle_plate && (
                <span className="error-text">{errors.vehicle_plate}</span>
              )}
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="form-section">
          <h4 className="section-title">📝 Ek Bilgiler</h4>
          <div className="form-group">
            <label htmlFor="notes">
              Notlar
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className={errors.notes ? 'error' : ''}
              placeholder="Taxifahrer hakkında ek notlar..."
              rows="3"
              disabled={isSubmitting}
            />
            {errors.notes && (
              <span className="error-text">{errors.notes}</span>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            ❌ İptal
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? (
              <>
                <span className="loading-spinner"></span>
                {isEditMode ? 'Güncelleniyor...' : 'Ekleniyor...'}
              </>
            ) : (
              <>
                {isEditMode ? '💾 Güncelle' : '➕ Ekle'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DriverForm;
